import { Plugin } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

import type { Comparison } from '#util/diffing/NodeDiff'

import * as asterisks from './asterisks'
import * as helpers from './helpers'

type PluginOptions = {
  comparison: Comparison
  useRightSideMarks?: boolean
  highlightAdditions?: boolean
  showDeletions?: boolean
}

function buildInlineDecorations(
  pluginState: helpers.RevisionPluginState,
  highlightAdditions: boolean,
  showDeletions: boolean,
): Decoration[] {
  return [
    ...(highlightAdditions
      ? pluginState.changedRanges.map(helpers.buildChangedDecoration)
      : []),
    ...pluginState.removedPositions.map((r) =>
      helpers.buildRemovedDecoration(r.position, showDeletions ? r.text : null),
    ),
  ]
}

export const inlineChangesPlugin = ({
  comparison,
  useRightSideMarks = false,
  highlightAdditions = true,
  showDeletions = false,
}: PluginOptions): Plugin<helpers.RevisionPluginState> => {
  return new Plugin({
    key: helpers.PLUGIN_KEY,
    state: {
      init() {
        const { changed, removed } = comparison.changesRelativeToRight()
        return {
          changedRanges: changed,
          removedPositions: removed,
          identical: comparison.identical,
        }
      },
      // we don't recompute anything
      apply(_tr, pluginState) {
        return pluginState
      },
    },
    props: {
      decorations(editorState) {
        const pluginState = helpers.PLUGIN_KEY.getState(editorState)
        if (pluginState) {
          return DecorationSet.create(
            editorState.doc,
            buildInlineDecorations(
              pluginState,
              highlightAdditions,
              showDeletions,
            ),
          )
        }
      },
    },
    view(editorView) {
      const pluginState = helpers.PLUGIN_KEY.getState(editorView.state)
      const asteriskData = asterisks.buildAsteriskData({
        editorView,
        changes: pluginState?.changedRanges ?? [],
        removals: pluginState?.removedPositions.map((r) => r.position) ?? [],
      })
      asterisks.insertAsteriskNodes(asteriskData, useRightSideMarks)

      return {
        update() {
          asterisks.removeAsteriskNodes()
          asterisks.insertAsteriskNodes(asteriskData, useRightSideMarks)
        },
        destroy() {
          asterisks.removeAsteriskNodes()
        },
      }
    },
  })
}

/**
 * Used for printing the script with inline changes. Adds gutter marks for
 * changed and removed lines, and highlights additions and deletions in the text.
 */
export const inlineChangesPrintPlugin = ({
  comparison,
  useRightSideMarks = false,
  highlightAdditions = true,
  showDeletions = false,
}: PluginOptions): Plugin<helpers.RevisionPluginState> => {
  return new Plugin({
    key: helpers.PLUGIN_KEY,
    state: {
      init() {
        const { changed, removed } = comparison.changesRelativeToRight()
        return {
          changedRanges: changed,
          removedPositions: removed,
          identical: comparison.identical,
        }
      },
      apply(tr, pluginState) {
        if (!tr.docChanged) return pluginState

        return {
          ...pluginState,
          changedRanges: pluginState.changedRanges.map(([from, to]) => [
            tr.mapping.map(from, 1),
            tr.mapping.map(to, -1),
          ]),
          removedPositions: pluginState.removedPositions.map((removal) => ({
            ...removal,
            position: tr.mapping.map(removal.position, 1),
          })),
        }
      },
    },
    props: {
      decorations(editorState) {
        const pluginState = helpers.PLUGIN_KEY.getState(editorState)
        if (!pluginState) return

        const inlineDecorations = buildInlineDecorations(
          pluginState,
          highlightAdditions,
          showDeletions,
        )

        const { changedRanges, removedPositions } = pluginState
        const { pageBlocks, pageLayout } =
          helpers.getBlockLayoutData(editorState)

        const side = useRightSideMarks ? 'right' : 'left'
        const gutterBlocks: helpers.GutterBlockMark[] = []

        for (const blockInfo of pageBlocks) {
          const { startPos } = blockInfo
          const block = editorState.doc.nodeAt(startPos)
          if (!block || block.textContent.length === 0) continue

          const marks: helpers.GutterMark[] =
            blockInfo.sections?.flatMap((section) =>
              section.map((s) => {
                const lineFrom = startPos + 1 + s.from
                const lineTo = startPos + 1 + s.to
                const hasChange = changedRanges.some(
                  ([from, to]) => from < lineTo && to > lineFrom,
                )
                if (hasChange) {
                  return {
                    character: helpers.ASTERISK_MARK,
                    className: helpers.GUTTER_MARK_REVISED_CLASS,
                  }
                }
                const hasRemoval = removedPositions.some(
                  (r) => r.position >= lineFrom && r.position <= lineTo,
                )
                if (hasRemoval) {
                  return {
                    character: helpers.ASTERISK_MARK,
                    className: helpers.GUTTER_MARK_REMOVED_CLASS,
                  }
                }
                return { character: ' ' }
              }),
            ) ?? []

          if (marks.some((m) => m.character !== ' ')) {
            gutterBlocks.push({
              containerPos: startPos,
              marks,
              side,
              ...helpers.resolveGutterMargins(blockInfo),
              height: marks.length,
              pageLayout,
            })
          }
        }

        const gutterDecorations = helpers.buildGutterDecorations(gutterBlocks)

        return DecorationSet.create(editorState.doc, [
          ...inlineDecorations,
          ...gutterDecorations,
        ])
      },
    },
  })
}
